import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { BarChart3, Star, ArrowUpDown } from "lucide-react";
import { FilterPills } from "@/components/FilterPills";

const PERIODS = [{ key: "30", label: "Last 30 days" }, { key: "90", label: "Last 90 days" }, { key: "0", label: "All time" }];
const COLS = [
  { k: "name", l: "Collector" },
  { k: "received", l: "Leads" },
  { k: "contacted", l: "Contacted" },
  { k: "confirmed", l: "Confirmed" },
  { k: "contact_rate", l: "Contact rate" },
  { k: "avg_response_minutes", l: "Avg response" },
  { k: "rating_avg", l: "Rating" },
  { k: "period_reviews", l: "Reviews" },
  { k: "open", l: "Open" },
];

const fmtMins = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.round(m / 1440)}d`;
};

const Rate = ({ v }) => v == null ? <span className="text-[#8A9A86]">—</span> : (
  <div className="flex items-center gap-2 min-w-[90px]">
    <div className="h-1.5 flex-1 rounded-full bg-[#1E3F33]/10 overflow-hidden"><div className="h-full bg-[#2D6A4F]" style={{ width: `${v}%` }} /></div>
    <span className="text-[#1E3F33] tabular-nums">{v}%</span>
  </div>
);

const Cells = ({ r }) => (
  <>
    <td className="px-3 py-2.5 tabular-nums">{r.received}</td>
    <td className="px-3 py-2.5 tabular-nums">{r.contacted}</td>
    <td className="px-3 py-2.5 tabular-nums">{r.confirmed}</td>
    <td className="px-3 py-2.5"><Rate v={r.contact_rate} /></td>
    <td className="px-3 py-2.5 tabular-nums">{fmtMins(r.avg_response_minutes)}</td>
    <td className="px-3 py-2.5 whitespace-nowrap">{r.rating_count ? <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#CC5A3A] text-[#CC5A3A]"/>{r.rating_avg.toFixed(1)} <span className="text-[#8A9A86]">({r.rating_count})</span></span> : <span className="text-[#8A9A86]">New</span>}</td>
    <td className="px-3 py-2.5 tabular-nums">{r.period_reviews}</td>
    <td className="px-3 py-2.5 tabular-nums">{r.open}</td>
  </>
);

const sortRows = (rows, key, dir) => [...rows].sort((a, b) => {
  const av = a[key] ?? -1, bv = b[key] ?? -1;
  const c = typeof av === "string" ? av.localeCompare(bv) : av - bv;
  return dir === "asc" ? c : -c;
});

export const TeamPerformance = () => {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "received", dir: "desc" });
  useEffect(() => { api.get("/company/performance", { params: { days } }).then(({ data: d }) => setData(d)); }, [days]);
  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="card-soft" data-testid="team-performance-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="font-heading text-xl text-[#1E3F33] flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#CC5A3A]"/> Team performance</h2>
        <FilterPills options={PERIODS} value={days} onChange={setDays} testidPrefix="performance-period" />
      </div>
      <p className="text-xs text-[#8A9A86] mb-4">Contact rate = leads the collector marked as contacted ÷ leads received. Avg response = time from lead to first contact. Only leads received while listed under your company are counted.</p>
      {!data ? <p className="text-sm text-[#5C6B64]">Loading…</p> : data.rows.length === 0 ? (
        <p className="text-sm text-[#8A9A86]" data-testid="performance-empty">Add collectors to see their performance here.</p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="performance-table">
            <thead className="text-left text-[#5C6B64] text-xs"><tr>
              {COLS.map((c) => (
                <th key={c.k} className="px-3 py-2 font-medium whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort(c.k)} data-testid={`performance-sort-${c.k}`} className={`inline-flex items-center gap-1 hover:text-[#1E3F33] ${sort.key === c.k ? "text-[#CC5A3A]" : ""}`}>{c.l} <ArrowUpDown className="w-3 h-3"/></button>
                </th>
              ))}
            </tr></thead>
            <tbody className="text-[#1E3F33]">
              {sortRows(data.rows, sort.key, sort.dir).map((r) => (
                <tr key={r.collector_id} className="border-t border-[#1E3F33]/8" data-testid={`performance-row-${r.collector_id}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <Link to={`/dashboard/company/collectors/${r.collector_id}?tab=leads`} className="font-medium hover:text-[#CC5A3A]">{r.name}</Link>
                    {!r.receiving_leads && <div className="text-[10px] text-[#8A5A00]">Not receiving leads</div>}
                  </td>
                  <Cells r={r} />
                </tr>
              ))}
              <tr className="border-t-2 border-[#1E3F33]/15 font-medium bg-[#F0EFEA]/50" data-testid="performance-totals">
                <td className="px-3 py-2.5">Team total</td>
                <Cells r={data.totals} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
